import React, { useState, useRef, useEffect, useMemo } from 'react';
import { FaCode, FaSearch, FaChevronDown } from 'react-icons/fa';

/**
 * Variable-enabled input/textarea that lets users insert variable tokens
 * sourced from connected customAgent nodes.
 */
const VariableInput = ({
  as = 'input',
  type = 'text',
  value = '',
  onChange,
  placeholder = '',
  rows = 4,
  availableFields = [],
  disabled = false,
  className = '',
  style = {},
  buttonLabel = 'Insert variable',
  name
}) => {
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, right: 0 });

  const InputTag = as === 'textarea' ? 'textarea' : 'input';
  const hasVariables = Array.isArray(availableFields) && availableFields.length > 0;

  const filteredFields = useMemo(() => {
    if (!searchTerm) return availableFields;
    return availableFields.filter((field) => {
      const label = field.label || '';
      const optionValue = field.value || '';
      return (
        label.toLowerCase().includes(searchTerm.toLowerCase()) ||
        optionValue.toLowerCase().includes(searchTerm.toLowerCase())
      );
    });
  }, [availableFields, searchTerm]);

  // Calculate dropdown position when it opens
  useEffect(() => {
    if (!isOpen || !triggerRef.current) return;

    const calculatePosition = () => {
      const trigger = triggerRef.current;
      if (!trigger) return;

      const rect = trigger.getBoundingClientRect();
      const dropdownWidth = 320;
      const dropdownHeight = 320;
      const spacing = 6;
      
      // Check if there's enough space below
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      
      let top, right;
      
      if (spaceBelow >= dropdownHeight + spacing || spaceBelow >= spaceAbove) {
        // Position below
        top = rect.bottom + spacing;
      } else {
        // Position above, but ensure it doesn't go above viewport
        top = Math.max(16, rect.top - dropdownHeight - spacing);
      }
      
      // Ensure top doesn't go below viewport
      if (top + dropdownHeight > window.innerHeight - 16) {
        top = Math.max(16, window.innerHeight - dropdownHeight - 16);
      }
      
      // Align to the right edge of the trigger button
      // Calculate right position from viewport right edge
      right = window.innerWidth - rect.right;
      
      // Ensure dropdown doesn't overflow on the left side
      const leftPosition = rect.right - dropdownWidth;
      if (leftPosition < 16) {
        // If it would overflow, align to left edge with padding
        right = window.innerWidth - 16 - dropdownWidth;
      }
      
      // Ensure right position is valid
      right = Math.max(16, right);
      
      setDropdownPosition({ top, right });
    };

    calculatePosition();
    
    // Recalculate on scroll or resize
    window.addEventListener('scroll', calculatePosition, true);
    window.addEventListener('resize', calculatePosition);
    
    const handleClickOutside = (event) => {
      const inputEl = inputRef.current;
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        inputEl !== event.target &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    
    return () => {
      window.removeEventListener('scroll', calculatePosition, true);
      window.removeEventListener('resize', calculatePosition);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleValueChange = (event) => {
    onChange?.(event.target.value);
  };

  const insertVariable = (variableValue) => {
    const inputEl = inputRef.current;
    if (!inputEl) {
      onChange?.(variableValue);
      return;
    }

    const selectionStart = inputEl.selectionStart ?? value.length;
    const selectionEnd = inputEl.selectionEnd ?? value.length;

    const newValue = `${value?.slice(0, selectionStart) || ''}${variableValue}${
      value?.slice(selectionEnd) || ''
    }`;

    onChange?.(newValue);

    requestAnimationFrame(() => {
      inputEl.focus();
      const caretPosition = selectionStart + variableValue.length;
      inputEl.setSelectionRange(caretPosition, caretPosition);
    });
  };

  const handleVariableSelect = (field) => {
    insertVariable(field.value);
    setIsOpen(false);
    setSearchTerm('');
  };

  return (
    <div className={`variable-input ${className}`} style={style}>
      <InputTag
        ref={inputRef}
        type={as === 'textarea' ? undefined : type}
        rows={as === 'textarea' ? rows : undefined}
        value={value}
        onChange={handleValueChange}
        placeholder={placeholder}
        disabled={disabled}
        name={name}
      />

      <div className="variable-picker-container">
        <button
          ref={triggerRef}
          type="button"
          className="variable-picker-trigger"
          onClick={() => setIsOpen((prev) => !prev)}
          disabled={!hasVariables || disabled}
          title={hasVariables ? 'Insert variable' : 'Connect an upstream node to unlock variables'}
        >
          <FaCode />
          <span>{buttonLabel}</span>
          <FaChevronDown className={`picker-chevron ${isOpen ? 'open' : ''}`} />
        </button>

        {isOpen && (
          <div 
            className="variable-picker-dropdown" 
            ref={dropdownRef}
            style={{
              top: `${dropdownPosition.top}px`,
              right: `${dropdownPosition.right}px`
            }}
          >
            <div className="variable-picker-search">
              <FaSearch />
              <input
                type="search"
                placeholder="Search variables"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
              />
            </div>
            <div className="variable-picker-list">
              {filteredFields.length === 0 ? (
                <div className="variable-picker-empty">No variables found</div>
              ) : (
                filteredFields.map((field) => (
                  <button
                    type="button"
                    key={`${field.value}-${field.label}`}
                    className="variable-picker-option"
                    onClick={() => handleVariableSelect(field)}
                  >
                    <div className="variable-picker-option-text">
                      <span className="variable-picker-option-label">{field.label}</span>
                      {field.group && (
                        <span className="variable-picker-option-group">{field.group}</span>
                      )}
                    </div>
                    <code>{field.value}</code>
                  </button>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VariableInput;

